/**
 * UserManagementPage
 * Admin page for managing users, roles, and permissions
 */
import { useState, useMemo, useEffect } from 'react'
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useUserList } from '@/hooks/useUsers'
import { RoleBadge } from '@/components/admin/RoleBadge'
import { UserListSkeleton } from '@/components/admin/UserListSkeleton'
import { UserDetailDrawer } from '@/components/admin/UserDetailDrawer'
import { DataTablePagination } from '@/components/data-display/DataTablePagination'
import { RelativeTime } from '@/components/data-display/RelativeTime'
import { ROLE_OPTIONS } from '@/types/admin'
import type { RoleCode, UserSortField, SortDirection } from '@/types/admin'
import { useDebounce } from '@/hooks/useDebounce'

const DEFAULT_PAGE_SIZE = 20

/**
 * User Management admin page
 */
export function UserManagementPage() {
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleCode | 'all'>('all')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortBy, setSortBy] = useState<UserSortField | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  
  const debouncedSearch = useDebounce(search, 300)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, roleFilter])

  const queryParams = useMemo(() => ({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: debouncedSearch || undefined,
    role: roleFilter !== 'all' ? roleFilter : undefined,
    sortBy,
    sortDirection: sortBy ? sortDirection : undefined,
  }), [debouncedSearch, roleFilter, page, pageSize, sortBy, sortDirection])

  const { data, isLoading, error } = useUserList(queryParams)

  const handleRowClick = (userId: string) => {
    setSelectedUserId(userId)
  }

  const handleDrawerClose = () => {
    setSelectedUserId(null)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1) // Reset to first page when page size changes
  }

  const handleSort = (field: UserSortField) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to descending for dates
      setSortBy(field)
      setSortDirection('desc')
    }
    setPage(1) // Reset to first page when sorting changes
  }

  const getSortIcon = (field: UserSortField) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-muted-foreground">
            Manage user roles and permissions
          </p>
        </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={roleFilter}
          onValueChange={(value) => setRoleFilter(value as RoleCode | 'all')}
        >
          <SelectTrigger className="w-48" aria-label="Filter by role">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Error State */}
      {error && (
        <div className="text-center py-8 text-muted-foreground">
          <p>Failed to load users.</p>
          <p className="text-sm">Please try again later.</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && <UserListSkeleton />}

      {/* Empty State */}
      {!isLoading && !error && data?.users.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>No users found</p>
          <p className="text-sm">Try adjusting your search or filters.</p>
        </div>
      )}

      {/* User Table */}
      {!isLoading && !error && data && data.users.length > 0 && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead 
                    className="cursor-pointer select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center">
                      Created
                      {getSortIcon('createdAt')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none"
                    onClick={() => handleSort('lastLoginAt')}
                  >
                    <div className="flex items-center">
                      Last Login
                      {getSortIcon('lastLoginAt')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map((user) => (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(user.id)}
                  >
                    <TableCell className="font-medium">
                      {user.displayName}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} size="sm" />
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          user.isActive
                            ? 'text-green-600'
                            : 'text-red-600'
                        }
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <RelativeTime date={user.createdAt} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.lastLoginAt ? (
                        <RelativeTime date={user.lastLoginAt} />
                      ) : (
                        <span className="text-muted-foreground/50">Never</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <DataTablePagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50]}
            itemName="users"
          />
        </>
      )}

      {/* User Detail Drawer */}
      {selectedUserId && (
        <UserDetailDrawer
          open={!!selectedUserId}
          onOpenChange={(open) => !open && handleDrawerClose()}
          userId={selectedUserId}
        />
      )}
      </div>
    </PageShell>
  )
}
